import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import { EffectState } from '@/pixi/filters/types'

export class NeonFilter extends BaseSeratoFilter {
  constructor (effectState: EffectState) {
    const shader = `
    #define lineThreshold 0.2

    // Built in parameters
    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;
    uniform highp vec4 inputSize;

    uniform float contrast;

    // Conversion from rgb to hsv
    vec3 rgb2hsv(vec3 c)
    {
      vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
      vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
      vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));

      float d = q.x - min(q.w, q.y);
      float e = 1.0e-10;
      return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
    }

    // Coversion from hsv to rgb
    vec3 hsv2rgb(vec3 c)
    {
      vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
      vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
      return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
    }

    // Get luma from RGB
    float luma(vec3 c) {
      return dot(c, vec3(.2126, 0.7152, 0.0722));
    }

    mat4 contrastMatrix()
    {
        float contrast = contrast;
        if (contrast < 0.15) {
          contrast = 0.15;
        }

        float t = ( 1.0 - contrast ) / 2.0;
        return mat4( contrast, 0, 0, 0,
                     0, contrast, 0, 0,
                     0, 0, contrast, 0,
                     t, t, t, 1 );
    }

    /**
     * The sobel filter works by running 2x 3x3 kernels, one to detect a gradient in the x axis and one
     * to detect a gradient on the y axis. Although the kernel is 3x3, the middle row horizontally/vertically
     * are zeroes, so we omit them completely for performance. The kernel is as follows:
     * X axis:
     * | -1  |  0  |  1  |
     * | -2  |  0  |  2  |
     * | -1  |  0  |  1  |
     * 
     * Y axis:
     * | -1  | -2  | -1  |
     * |  0  |  0  |  0  |
     * |  1  |  2  |  1  |
     * 
     * The current pixel position is the center of the kernel, with the top left being -1, -1 away from the current
     * pixel and bottom right being 1, 1 away.
     * 
     */
    void main(void)
    {
      // One texel in UV space
      vec2 texelSize = 1.0 / inputSize.xy;

      // Accumulator for gradient in X axis
      vec4 x = vec4(0.0);

      // Accumulator for gradient in Y axis
      vec4 y = vec4(0.0);

      // Run the sobel kernel on the X axis
      x += texture2D(uSampler, vTextureCoord + vec2(-texelSize.x, -texelSize.y)) * -1.0;
      x += texture2D(uSampler, vTextureCoord + vec2(-texelSize.x, 0.0)) * -2.0;
      x += texture2D(uSampler, vTextureCoord + vec2(-texelSize.x, texelSize.y)) * -1.0;

      x += texture2D(uSampler, vTextureCoord + vec2(texelSize.x, -texelSize.y)) * 1.0;
      x += texture2D(uSampler, vTextureCoord + vec2(texelSize.x, 0.0)) * 2.0;
      x += texture2D(uSampler, vTextureCoord + vec2(texelSize.x, texelSize.y)) * 1.0;

      // Run the sobel kernel on the Y axis
      y += texture2D(uSampler, vTextureCoord + vec2(-texelSize.x, -texelSize.y)) * -1.0;
      y += texture2D(uSampler, vTextureCoord + vec2(0.0, -texelSize.y)) * -2.0;
      y += texture2D(uSampler, vTextureCoord + vec2(texelSize.x, -texelSize.y)) * -1.0;

      y += texture2D(uSampler, vTextureCoord + vec2(-texelSize.x, texelSize.y)) * 1.0;
      y += texture2D(uSampler, vTextureCoord + vec2(0.0, texelSize.y)) * 2.0;
      y += texture2D(uSampler, vTextureCoord + vec2(texelSize.x, texelSize.y)) * 1.0;

      // Get the luma value for each axis
      float lumaX = luma(x.rgb);
      float lumaY = luma(y.rgb);

      // Calculate a final 'intensity' from the X/Y axis (this is the hypotenuse given X and Y)
      float intensity = sqrt(lumaX * lumaX + lumaY * lumaY);

      vec4 originalColor = texture2D(uSampler, vTextureCoord);
      
      // Boost the line intensity if above the line threshold
      intensity = intensity > lineThreshold ? intensity * 2.0 : intensity;

      // Get the colour from the texture coordinate, maximise saturation and value, and then multiply by intensity
      vec3 color = (originalColor * contrastMatrix()).rgb;

      // hsv is mapped to xyz here
      vec3 hsv = rgb2hsv(color);
      hsv.y = 1.0;
      hsv.z = 1.0;
      color = hsv2rgb(hsv) * intensity;

      // Setting alpha to 0 filters out everything besides the neon lines.
      gl_FragColor = vec4(color, intensity);
    }`

    super(
      effectState,
      undefined,
      shader,
      {
        isAudioReactive: false
      }
    )
  }

  public updateUniforms () {
    this.uniforms.contrast = this.effectState.intensity
  }
}
